import { useEffect, useMemo, useState } from "react";
import Seo from "@/components/Seo";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { ArtistChapter } from "@/components/ArtistChapter";
import { GalleryChapter } from "@/components/GalleryChapter";
import { WorldChapter } from "@/components/WorldChapter";
import { PracticeChapter } from "@/components/PracticeChapter";
import { RecognitionChapter } from "@/components/RecognitionChapter";
import { ExhibitionsChapter } from "@/components/ExhibitionsChapter";
import { ContactChapter } from "@/components/ContactChapter";
import { FaqSection } from "@/components/FaqSection";
import { fetchArtworks, fetchEvents, fetchSettings } from "@/lib/api";
import { ARTIST } from "@/lib/constants";
import { HOME, PAGES, apiAssetUrl, isHonour } from "@/lib/site";
import { sectionSchema } from "@/lib/schema";
import { scrollTop } from "@/lib/smooth";

// One chapter of Home on its own URL (/about, /gallery, …), so each can be
// indexed and shown as a sitelink. `section` is a key of PAGES.
export default function SectionPage({ section }) {
  const page = PAGES[section];
  const [artworks, setArtworks] = useState([]);
  const [events, setEvents] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    scrollTop(true);
    if (section === "gallery") fetchArtworks().then(setArtworks).catch(() => setArtworks([]));
    if (section === "recognition" || section === "exhibitions") fetchEvents().then(setEvents).catch(() => setEvents([]));
    if (section === "artist") fetchSettings().then(setSettings).catch(() => {});
  }, [section]);

  const jsonLd = useMemo(() => sectionSchema(section, { artworks }), [section, artworks]);
  const image = section === "gallery" && artworks[0] ? apiAssetUrl(artworks[0].image) : page.image || HOME.image;
  const trail = [
    { name: HOME.name, path: "/" },
    { name: page.name, path: page.path },
  ];

  const chapter = {
    artist: <ArtistChapter quote={settings?.artist_quote} headingAs="h1" />,
    gallery: <GalleryChapter artworks={artworks} headingAs="h1" />,
    world: <WorldChapter headingAs="h1" />,
    practice: <PracticeChapter headingAs="h1" />,
    recognition: <RecognitionChapter honours={events.filter(isHonour)} headingAs="h1" />,
    exhibitions: <ExhibitionsChapter events={events.filter((e) => !isHonour(e))} headingAs="h1" />,
    contact: <ContactChapter headingAs="h1" />,
  }[section];

  return (
    <div data-testid={`${section}-page`}>
      <Seo
        title={page.title}
        siteName={ARTIST.name}
        description={page.description}
        image={image}
        jsonLd={jsonLd}
      />
      <Navbar />
      <main className="pt-28 sm:pt-32">
        <Breadcrumbs trail={trail} className="mx-auto max-w-7xl px-5 sm:px-8" />
        {chapter}
        {section === "world" && <FaqSection />}
      </main>
      <Footer />
    </div>
  );
}
